// ImportRunner — the stripped-down "run a saved import" surface (Imports tab →
// Run). No configuration: the saved definition already specifies the source
// options, column mapping, strategy and destination. The user just sees the
// expected columns (+ a downloadable .xlsx template), picks a file, and imports.
// The file's headers must EXACTLY match the definition's input columns — a
// mismatch is refused with the missing/extra columns named.
//
// Authorisation is the import GRANT (the def-scoped analyze/run endpoints check
// it), so a granted user need not have direct DB access to the target.
//
// Props: defId, defName, table, definition (parsed config), onClose()
import { createSignal, createMemo, Show, For } from 'solid-js';
import Modal from './Modal.jsx';
import ProgressModal from './ProgressModal.jsx';
import GeneratedSqlModal from './GeneratedSqlModal.jsx';
import DateFormatPicker from './DateFormatPicker.jsx';
import { looksLikeDate } from '../lib/dateformats.js';
import { analyzeImportDef, runImport, resolveImport } from '../lib/import.js';
import { downloadImportTemplate } from '../lib/importdefs.js';
import { toast } from '../lib/toast.js';

export default function ImportRunner(props) {
  const def = props.definition || {};
  const isSql = () => (def.destination || 'execute') === 'sql';
  const isEachRow = () => (def.destination || 'execute') === 'eachrow';
  const isPerRow = () => isSql() || isEachRow(); // both bind file columns as @v…
  const titleCaseVar = (s) => {
    const p = String(s).split(/[_\-\s]+/).filter(Boolean);
    return p.length ? p.map((x) => x[0].toUpperCase() + x.slice(1).toLowerCase()).join('') : s;
  };
  // Expected file columns. INSERT modes: the input/enum mapped source headers.
  // SQL mode: the captured source headers (each becomes @v<header>) — empty for
  // definitions saved before headers were captured.
  const expectedHeaders = () =>
    isPerRow()
      ? (def.sourceHeaders || [])
      : (def.columns || []).filter((c) => (c.mode === 'input' || c.mode === 'enum') && c.source).map((c) => c.source);

  const [onError, setOnError] = createSignal(def.onError === 'ask' ? 'ask' : 'skip');
  const [file, setFile] = createSignal(null);
  const [analysis, setAnalysis] = createSignal(null);
  const [token, setToken] = createSignal(null);
  const [busy, setBusy] = createSignal(false);
  const [uploadPct, setUploadPct] = createSignal(null);
  const [jobToken, setJobToken] = createSignal(null);
  const [generatedSql, setGeneratedSql] = createSignal(null);
  const [dateFormats, setDateFormats] = createSignal({ ...(def.dateFormats || {}) }); // file header → format key
  const setDF = (header, key) => setDateFormats((m) => {
    const n = { ...m };
    if (key) n[header] = key; else delete n[header];
    return n;
  });
  const sampleFor = (header) => {
    const a = analysis();
    if (!a) return '';
    const i = (a.headers || []).indexOf(header);
    if (i < 0) return '';
    for (const row of (a.previewRows || [])) {
      if (row[i] != null && String(row[i]).trim() !== '') return row[i];
    }
    return '';
  };
  // Candidates: columns the admin tagged with a format, or whose samples look like dates.
  const dateColumns = () => {
    const a = analysis();
    if (!a) return [];
    return (a.headers || []).map((h) => {
      const sample = sampleFor(h);
      return { header: h, sample, candidate: !!dateFormats()[h] || looksLikeDate(sample) };
    });
  };

  // Header validation: the file must CONTAIN every expected input column
  // (case-insensitive). Extra columns are fine — they're simply not imported
  // (the same columns the admin marked "skip"), so they never block the run.
  const headerCheck = createMemo(() => {
    const a = analysis();
    if (!a) return null;
    if (isPerRow()) return { ok: true, missing: [], ignored: [] }; // per-row: any file — its columns become @v
    const norm = (s) => String(s).trim().toLowerCase();
    const fileSet = new Set((a.headers || []).map(norm).filter(Boolean));
    const expSet = new Set(expectedHeaders().map(norm));
    const missing = [...expSet].filter((h) => !fileSet.has(h));
    const ignored = [...fileSet].filter((h) => !expSet.has(h));
    return { ok: missing.length === 0, missing, ignored };
  });

  const analyze = async (f) => {
    setFile(f); setToken(null); setAnalysis(null);
    if (!f) return;
    setBusy(true); setUploadPct(0);
    try {
      const r = await analyzeImportDef(props.defId, f, def.options || {},
        (loaded, total) => setUploadPct(total ? Math.round((loaded / total) * 100) : 0));
      setToken(r.token); setAnalysis(r.analysis);
    } catch (e) {
      toast.error('Analyze failed: ' + (e?.message || e));
    } finally { setBusy(false); setUploadPct(null); }
  };

  const run = async () => {
    if (!token() || !headerCheck()?.ok) return;
    setBusy(true);
    try {
      const dest = def.destination || 'execute';
      const r = await runImport(token(), {
        mode: dest, options: def.options || {}, columns: def.columns || [],
        strategy: def.strategy || 'insert', nullToken: def.nullToken || '',
        emptyAsEmpty: !!def.emptyAsEmpty, onError: onError(),
        rowSql: def.rowSql || '', dateFormats: dateFormats(), fileName: file()?.name || '',
      });
      if (dest === 'generate') {
        if (r.truncated) toast.info(`Generated ${r.rows} rows (capped).`);
        setGeneratedSql(r.sql || '');
      } else {
        setJobToken(r.token);
      }
    } catch (e) {
      toast.error('Import failed: ' + (e?.message || e));
    } finally { setBusy(false); }
  };

  // Retry after a failed/aborted run. The backend deletes the uploaded temp file
  // after each run, so we re-analyze the retained File (a fresh upload) and re-run
  // — the user never re-picks the file.
  const retry = async () => {
    const f = file();
    if (!f) { toast.error('No file to retry — re-select the file.'); return; }
    setJobToken(null);  // tear down the progress modal → back to the form
    await analyze(f);   // re-upload the picked File → fresh token
    if (!token()) {
      // The re-upload failed — almost always because the file was EDITED on disk
      // since you picked it (the browser won't re-read a changed selection). The
      // form is back up: re-select the file to import the updated version.
      toast.info('Re-select your file below to import the updated version.');
      return;
    }
    if (headerCheck()?.ok) await run();
  };

  return (
    <Show when={!jobToken()} fallback={
      <ProgressModal token={jobToken()} title={`Importing — ${props.defName || props.table}`} kind="import"
        onResolve={(a) => resolveImport(jobToken(), a)} onRetry={retry} onClose={props.onClose} />
    }>
      <Show when={!generatedSql()} fallback={<GeneratedSqlModal sql={generatedSql()} onClose={props.onClose} />}>
        <Modal open={true} title={`Run import — ${props.defName || props.table}`} onClose={props.onClose}>
          <div class="import-dialog">
            <Show when={!isPerRow()}>
              <div class="import-expected">
                <div class="import-section-title">
                  Expected columns <span class="muted">— your file must include these headers (extra columns are ignored)</span>
                </div>
                <div class="import-preview-wrap">
                  <table class="import-preview">
                    <thead><tr><For each={expectedHeaders()}>{(h) => <th>{h}</th>}</For></tr></thead>
                  </table>
                </div>
                <button class="btn btn-sm btn-ghost"
                        onClick={() => downloadImportTemplate(props.defId, `${props.table}-import-template.xlsx`)}>
                  ⬇ Download proper format (.xlsx)
                </button>
              </div>
            </Show>
            <Show when={isPerRow()}>
              <div class="import-expected">
                <Show when={!isEachRow()}>
                  <div class="import-section-title">Runs this SQL once per row:</div>
                  <textarea class="input import-sql-out" readonly rows="6">{def.rowSql || ''}</textarea>
                </Show>
                <Show when={expectedHeaders().length}>
                  <div class="import-preview-wrap">
                    <table class="import-preview">
                      <thead><tr><For each={expectedHeaders()}>{(h) => <th>{h}</th>}</For></tr></thead>
                    </table>
                  </div>
                  <button class="btn btn-sm btn-ghost"
                          onClick={() => downloadImportTemplate(props.defId, `${props.table}-import-template.xlsx`)}>
                    ⬇ Download proper format (.xlsx)
                  </button>
                </Show>
                <Show when={analysis()}>
                  <div class="muted" style="font-size:12px;">
                    Your file's columns are bound as:{' '}
                    <span class="mono">{(analysis().headers || []).map((h) => '@v' + titleCaseVar(h)).join(', ')}</span>
                  </div>
                </Show>
              </div>
            </Show>

            <label class="restore-field">
              <span class="restore-field-label">Your file (.csv or .xlsx)</span>
              <input type="file" accept=".csv,.tsv,.xlsx" disabled={busy()}
                     onChange={(e) => analyze(e.currentTarget.files?.[0] || null)} />
            </label>
            <Show when={uploadPct() !== null}>
              <div class="progress-track"><div class="progress-fill" style={`width:${uploadPct()}%`} /></div>
            </Show>

            <Show when={analysis()}>
              <Show when={headerCheck() && !headerCheck().ok}>
                <div class="import-warn">
                  This file is missing required columns: <span class="mono">{headerCheck().missing.join(', ')}</span>
                  <div class="muted" style="font-size:12px;">Download the proper format above, fill it in, and try again.</div>
                </div>
              </Show>
              <Show when={headerCheck()?.ok && headerCheck().ignored.length}>
                <div class="muted" style="font-size:12px;">
                  Extra columns will be ignored: <span class="mono">{headerCheck().ignored.join(', ')}</span>
                </div>
              </Show>
              <Show when={(analysis().previewRows || []).length}>
                <div class="import-section-title">Preview <span class="muted">(first {analysis().previewRows.length} rows)</span></div>
                <div class="import-preview-wrap">
                  <table class="import-preview">
                    <thead><tr><For each={analysis().headers}>{(h) => <th>{h}</th>}</For></tr></thead>
                    <tbody><For each={analysis().previewRows}>
                      {(row) => <tr><For each={analysis().headers}>{(_h, i) => <td>{row[i()] ?? ''}</td>}</For></tr>}
                    </For></tbody>
                  </table>
                </div>
              </Show>

              <DateFormatPicker columns={dateColumns()} value={dateFormats()} onChange={setDF} />
            </Show>

            <Show when={(def.destination || 'execute') !== 'generate'}>
              <div class="import-section-title">On a bad row</div>
              <div class="import-radios">
                <label class="import-radio">
                  <input type="radio" name="runerr" checked={onError() === 'skip'} onChange={() => setOnError('skip')} />
                  <span>Skip &amp; report <span class="muted">(keep going; download a CSV of rejected rows after)</span></span>
                </label>
                <label class="import-radio">
                  <input type="radio" name="runerr" checked={onError() === 'ask'} onChange={() => setOnError('ask')} />
                  <span>Ask each time <span class="muted">(pause on each error)</span></span>
                </label>
              </div>
            </Show>

            <div class="restore-actions">
              <button class="btn btn-ghost" onClick={props.onClose} disabled={busy()}>Cancel</button>
              <button class="btn btn-primary" onClick={run} disabled={busy() || !token() || !headerCheck()?.ok}>⬆ Import</button>
            </div>
          </div>
        </Modal>
      </Show>
    </Show>
  );
}
